/**
 * use-print-config — dispatcher Print Settings data (Phase 10 / Task 10.6).
 *
 * GET  /v1/tenants/print-config       (VIEW_RUN_TICKETS) — header + hasLogo
 * PATCH /v1/tenants/print-config      (MANAGE_TENANT)     — set/clear the header or phone
 * POST  /v1/tenants/print-config/logo (MANAGE_TENANT)     — multipart logo upload
 * DELETE /v1/tenants/print-config/logo(MANAGE_TENANT)     — remove the logo
 *
 * This logo is the thermal one only (task #58): the sidebar, invoices and emails use the
 * company logo (features/company-logo), so a change here leaves them alone.
 */

import { useApiOpts, useApiQuery, useApiMutation, authedFetch } from '@lodex-pro/web-shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface PrintConfigResponse {
  readonly customHeader: string | null;
  readonly hasLogo: boolean;
  /** Task #29 — the phone printed on tickets, US E.164; null for none. */
  readonly phone: string | null;
}

const PRINT_CONFIG_KEY = ['print-config'] as const;

/** GET the current print config. */
export function usePrintConfig(
  enabled = true,
): ReturnType<typeof useApiQuery<PrintConfigResponse>> {
  const apiOpts = useApiOpts();
  return useApiQuery<PrintConfigResponse>(apiOpts, '/v1/tenants/print-config', {
    queryKey: PRINT_CONFIG_KEY,
    enabled,
    staleTime: 30_000,
  });
}

/** PATCH the custom header (`null` clears it). */
export function useUpdatePrintHeader(): ReturnType<
  typeof useApiMutation<{ customHeader: string | null }, PrintConfigResponse>
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ customHeader: string | null }, PrintConfigResponse>(
    apiOpts,
    '/v1/tenants/print-config',
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: PRINT_CONFIG_KEY });
      },
    },
  );
}

/**
 * Task #29 — PATCH the phone printed on tickets: US E.164, or `null` for none. The server
 * re-canonicalizes and refuses a number it cannot read (400 VALIDATION_FAILED).
 */
export function useUpdatePrintPhone(): ReturnType<
  typeof useApiMutation<{ phone: string | null }, PrintConfigResponse>
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ phone: string | null }, PrintConfigResponse>(
    apiOpts,
    '/v1/tenants/print-config',
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: PRINT_CONFIG_KEY });
      },
    },
  );
}

/** Upload a new logo (multipart). Throws with the server message (e.g. 422 conversion failure). */
export function useUploadPrintLogo(): ReturnType<
  typeof useMutation<PrintConfigResponse, Error, File>
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<PrintConfigResponse, Error, File>({
    mutationFn: async (file: File): Promise<PrintConfigResponse> => {
      const form = new FormData();
      form.append('file', file);
      const res = await authedFetch(apiOpts, '/v1/tenants/print-config/logo', {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        let message = 'Logo upload failed. Try again.';
        try {
          const body = (await res.json()) as { error?: { message?: string } };
          if (typeof body.error?.message === 'string') message = body.error.message;
        } catch {
          // keep the default message
        }
        throw new Error(message);
      }
      return (await res.json()) as PrintConfigResponse;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PRINT_CONFIG_KEY });
    },
  });
}

/** DELETE the logo. */
export function useDeletePrintLogo(): ReturnType<typeof useApiMutation<void, PrintConfigResponse>> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<void, PrintConfigResponse>(
    apiOpts,
    '/v1/tenants/print-config/logo',
    'DELETE',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: PRINT_CONFIG_KEY });
      },
    },
  );
}
